import { useButtonGroup, forwardRef } from '@chakra-ui/react';
import type { ButtonProps as ChakraButtonProps } from '@chakra-ui/react';
import { cloneElement } from 'react';
import type { ReactElement } from 'react';

import { type IconProps } from '@frontend/components';

import { Button as ChakraButton } from './ChakraButton';
import { DEFAULT_PROPS } from './const';
import type { ButtonSize } from './types';

const ICON_SIZES: Record<string, string> = {
  lg: '1.286em',
  md: '1.333em',
  sm: '1.167em',
} satisfies Record<ButtonSize, string>;

export interface ButtonProps extends ChakraButtonProps {
  icon?: ReactElement<IconProps>;
  leftIcon?: ReactElement<IconProps>;
  loading?: boolean;
}

export const Button = forwardRef<ButtonProps, 'button'>(
  (
    {
      size: sizeProp,
      icon: iconProp,
      leftIcon: leftIconProp,
      isDisabled: isDisabledProp,
      disabled, // block disabled from being passed through directly
      isLoading: isLoadingProp,
      loading, // block loading from being passed through directly
      ...restProps
    },
    ref,
  ) => {
    const buttonGroupContext = useButtonGroup();
    const { size: defaultSize } = DEFAULT_PROPS;
    const size = sizeProp ?? buttonGroupContext?.size ?? defaultSize;
    const isDisabled = isDisabledProp ?? disabled;
    const isLoading = isLoadingProp ?? loading;
    const leftIcon = leftIconProp;

    // Change icon 'size' prop in line with React-Icons.
    const icon = iconProp
      ? cloneElement(iconProp, {
          size: ICON_SIZES[size] ?? ICON_SIZES[defaultSize],
        })
      : undefined;

    return (
      <ChakraButton
        ref={ref}
        size={size}
        iconSpacing="0.25rem"
        leftIcon={leftIcon}
        rightIcon={icon}
        {...restProps}
        isLoading={isLoading}
        {...((isDisabled || isLoading) && {
          'aria-disabled': true,
          onClick: (e) => e.preventDefault(),
        })}
      />
    );
  },
);
Button.displayName = 'Button';
